import { Component, ReactNode, ErrorInfo } from "react";
import { AlertCircle } from "lucide-react";
import i18n from "@/lib/i18n";
import { Card } from "@/components/ui/primitives";

interface Props {
  children: ReactNode;
  /** Korte, al vertaalde naam zodat de gebruiker ziet WELK onderdeel faalde. */
  sectionName: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Soft error boundary for individual page sections. Unlike the top-level
 * ErrorBoundary (which shows a full-screen "something went wrong" panel),
 * this one renders a small inline alert card and lets the rest of the
 * page continue. Use to isolate experimental / new sections so a query
 * to a not-yet-deployed table doesn't take down the entire surface.
 *
 * Example: when migrations land in dev but not yet in Lovable preview,
 * the progress_photos query throws; wrapping ProgressPhotoGrid in this
 * shows "Photos unavailable" instead of nuking the Progress page.
 *
 * Class component, dus geen useTranslation: de copy komt via i18n.t()
 * zodat het kaartje in de taal van de gebruiker staat.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[SectionErrorBoundary:${this.props.sectionName}]`, error);
    console.error("Component stack:", info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="p-4 border-warn/30 bg-warn/5" role="alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-warn mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-text">
                {i18n.t("sectionError.title", { section: this.props.sectionName })}
              </p>
              <p className="text-xs text-muted">
                {i18n.t("sectionError.body")}
                {this.state.error?.message ? ` (${this.state.error.message.slice(0, 120)})` : ""}
              </p>
            </div>
          </div>
        </Card>
      );
    }
    return this.props.children;
  }
}
